import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Typography, IconButton, Collapse, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { PackagingSolution, Product, ProductPlacement, Dimensions, BRAND_COLORS, MeasurementUnit } from '../types/index.ts';
import { getAllBoxes } from '../services/database.ts';

// Conversion constants (exact inverses so unit round-trips are stable).
const INCH_TO_CM = 2.54;
const CM_TO_INCH = 1 / 2.54;

// Same clearance as the calculator (0 = exact fit allowed), in inches — the
// whole 3D scene is built in inches because the placements are in inches.
const SAFETY_MARGIN = 0;

// Products are drawn slightly smaller than their real footprint so that
// touching products remain visually distinct.
const VISUAL_INSET = 0.97;

type OrientationType = 'standard' | 'tilted' | 'length-rotated' | 'width-rotated' | 'flipped' | 'combined-rotation';

interface BoxVisualizationProps {
  solution: PackagingSolution;
  product: Product | Product[];
  unitType?: 'cm' | 'inch'; // display unit for labels
}

const PRODUCT_COLORS = [
  '#E57373', '#64B5F6', '#81C784', '#FFD54F', '#7986CB',
  '#4DB6AC', '#FF8A65', '#9575CD', '#F06292', '#A1887F',
];

const getProductColor = (index: number): string => PRODUCT_COLORS[index % PRODUCT_COLORS.length];

const getOrientationLabel = (orientation: OrientationType): string => {
  switch (orientation) {
    case 'standard': return 'Standard';
    case 'tilted': return 'Tilted';
    case 'length-rotated': return 'Length Rotated';
    case 'width-rotated': return 'Width Rotated';
    case 'flipped': return 'Flipped';
    case 'combined-rotation': return 'Combined Rotation';
    default: return orientation;
  }
};

const getOrientationColor = (orientation: OrientationType): string => {
  switch (orientation) {
    case 'standard': return BRAND_COLORS.primary;
    case 'tilted': return BRAND_COLORS.secondary;
    case 'length-rotated': return '#9C27B0';
    case 'width-rotated': return '#2196F3';
    case 'flipped': return '#FF5722';
    case 'combined-rotation': return '#00BCD4';
    default: return BRAND_COLORS.primary;
  }
};

const toInches = (dimensions: Dimensions, unit: MeasurementUnit | undefined): Dimensions =>
  unit === 'cm'
    ? { length: dimensions.length * CM_TO_INCH, width: dimensions.width * CM_TO_INCH, height: dimensions.height * CM_TO_INCH }
    : dimensions;

// Oriented (length, width, height) for a product, matching the calculator's mapping.
const orientedDimensions = (d: Dimensions, orientation: OrientationType, box: Dimensions): Dimensions => {
  const candidates: Dimensions[] =
    orientation === 'length-rotated' ? [{ length: d.length, width: d.height, height: d.width }] :
    orientation === 'width-rotated' ? [{ length: d.width, width: d.height, height: d.length }] :
    orientation === 'flipped' ? [{ length: d.height, width: d.width, height: d.length }] :
    orientation === 'combined-rotation' ? [
      { length: d.height, width: d.length, height: d.width },
      { length: d.width, width: d.length, height: d.height },
      { length: d.length, width: d.width, height: d.height },
    ] :
    [{ length: d.length, width: d.width, height: d.height }];

  let best = candidates[0];
  let bestCount = -1;
  for (const c of candidates) {
    const count =
      Math.max(0, Math.floor((box.length - SAFETY_MARGIN) / c.length)) *
      Math.max(0, Math.floor((box.width - SAFETY_MARGIN) / c.width)) *
      Math.max(0, Math.floor((box.height - SAFETY_MARGIN) / c.height));
    if (count > bestCount) {
      bestCount = count;
      best = c;
    }
  }
  return best;
};

// Fallback grid placements for legacy share links that carry no placements.
const computeFallbackPlacements = (
  products: Product[],
  boxInches: Dimensions,
  orientation: OrientationType,
  maxCount: number
): ProductPlacement[] => {
  const placements: ProductPlacement[] = [];
  if (products.length === 0) return placements;

  const dims = orientedDimensions(toInches(products[0].dimensions, products[0].unit), orientation, boxInches);
  const nW = Math.max(0, Math.floor((boxInches.width - SAFETY_MARGIN) / dims.width));
  const nH = Math.max(0, Math.floor((boxInches.height - SAFETY_MARGIN) / dims.height));
  const nL = Math.max(0, Math.floor((boxInches.length - SAFETY_MARGIN) / dims.length));

  const startX = -boxInches.width / 2;
  const startY = -boxInches.height / 2;
  const startZ = -boxInches.length / 2;
  const target = Math.min(maxCount, nW * nH * nL, 500);

  let count = 0;
  for (let z = 0; z < nL && count < target; z++) {
    for (let y = 0; y < nH && count < target; y++) {
      for (let x = 0; x < nW && count < target; x++) {
        placements.push({
          productIndex: 0,
          dimensions: dims,
          position: {
            x: startX + dims.width / 2 + dims.width * x,
            y: startY + dims.height / 2 + dims.height * y,
            z: startZ + dims.length / 2 + dims.length * z,
          },
        });
        count++;
      }
    }
  }
  return placements;
};

// Dispose all geometries and materials before dropping a scene's children.
const clearScene = (scene: THREE.Scene) => {
  while (scene.children.length > 0) {
    const child = scene.children[0];
    scene.remove(child);
    if (child instanceof THREE.Mesh) {
      child.geometry?.dispose();
      const material = child.material;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose();
    }
  }
};

const BoxVisualization: React.FC<BoxVisualizationProps> = ({ solution, product: productProp, unitType = 'inch' }) => {
  // Stable identity: without useMemo this array is re-created on every render,
  // which re-runs the Three.js effect in a loop (renderer re-created, camera
  // reset, 100 % CPU) whenever a single product is displayed.
  const products = useMemo(
    () => (Array.isArray(productProp) ? productProp : [productProp]),
    [productProp]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const frameIdRef = useRef<number | null>(null);

  const [showLayoutDetails, setShowLayoutDetails] = useState<boolean>(false);
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const [fitError, setFitError] = useState<boolean>(false);
  const [displayedCount, setDisplayedCount] = useState<number>(0);

  // Box dimensions: from the local database when available, otherwise from the
  // solution itself (mandatory on the shared page, where the viewer's
  // localStorage does not contain the sender's boxes).
  const boxInches = useMemo((): Dimensions | null => {
    const localBox = getAllBoxes().find((b) => b.id === solution.boxId);
    if (localBox) return toInches(localBox.dimensions, localBox.unit || 'in');
    if (solution.boxDimensions) return toInches(solution.boxDimensions, solution.boxUnit || 'in');
    return null;
  }, [solution.boxId, solution.boxDimensions, solution.boxUnit]);

  const totalBoxes = Math.max(1, solution.boxInstances?.length || Math.min(solution.totalBoxesNeeded || 1, 50));

  const goToPreviousBox = useCallback(() => {
    setCurrentBoxIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);
  const goToNextBox = useCallback(() => {
    setCurrentBoxIndex((prev) => (prev < totalBoxes - 1 ? prev + 1 : prev));
  }, [totalBoxes]);

  // Réinitialiser sur TOUT changement de solution : après une nouvelle
  // recherche retombant sur la même caisse (même boxId), un index conservé
  // pointerait hors bornes (« Box 8 of 2 » rendu par la grille de secours).
  useEffect(() => {
    setCurrentBoxIndex(0);
  }, [solution]);

  const orientation: OrientationType =
    typeof solution.orientation === 'string' &&
    ['standard', 'tilted', 'length-rotated', 'width-rotated', 'flipped', 'combined-rotation'].includes(solution.orientation)
      ? (solution.orientation as OrientationType)
      : 'standard';

  // Placements for the current box instance (everything in inches).
  const placements = useMemo((): ProductPlacement[] => {
    const instancePlacements = solution.boxInstances?.find((instance) => instance.instanceIndex === currentBoxIndex)?.productPlacements;
    if (instancePlacements && instancePlacements.length > 0) return instancePlacements;
    if (currentBoxIndex === 0 && solution.productPlacements && solution.productPlacements.length > 0) return solution.productPlacements;
    if (!boxInches) return [];
    // Fallback grid (legacy links without placements): the LAST box only holds
    // what remains of the order, not a full box.
    const perBox = solution.productsPerBox || 1;
    const boxesNeeded = solution.totalBoxesNeeded || 1;
    const totalQuantity = products.reduce((sum, p) => sum + (p.quantity || 0), 0) || perBox * boxesNeeded;
    const countThisBox =
      currentBoxIndex === boxesNeeded - 1
        ? Math.max(1, totalQuantity - perBox * (boxesNeeded - 1))
        : perBox;
    return computeFallbackPlacements(products, boxInches, orientation, countThisBox);
  }, [solution, products, boxInches, orientation, currentBoxIndex]);

  const displayValue = (inches: number): string => (unitType === 'cm' ? inches * INCH_TO_CM : inches).toFixed(1);
  const displayProductValue = (value: number, unit: MeasurementUnit): string => {
    const inches = unit === 'cm' ? value * CM_TO_INCH : value;
    return displayValue(inches);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !boxInches) {
      setFitError(!boxInches);
      return;
    }

    const width = container.clientWidth || 400;
    const height = container.clientHeight || 400;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    // WebGL peut être indisponible (pilote, VM, navigateur) : ne jamais laisser
    // l'exception démonter toute l'application.
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (error) {
      console.warn('Box Finder: WebGL unavailable, 3D preview disabled.', error);
      setDisplayedCount(0);
      setFitError(true);
      return;
    }
    renderer.setClearColor(0xe6e9ed, 1); // Concrete deck — canvas melts into the page
    // Rendu net sur écrans HiDPI/Retina (borné à 2 pour la consommation GPU).
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // The box itself (wireframe) — scene space is inches.
    const boxGeometry = new THREE.BoxGeometry(boxInches.width, boxInches.height, boxInches.length);
    const boxMaterial = new THREE.MeshBasicMaterial({ color: BRAND_COLORS.primary, wireframe: true, transparent: true, opacity: 0.5 });
    scene.add(new THREE.Mesh(boxGeometry, boxMaterial));

    // The products, at their precomputed positions (inches as well). Les
    // géométries et matériaux sont partagés entre produits identiques :
    // 500 caisses identiques = 1 géométrie, pas 500.
    const geometryCache = new Map<string, THREE.BoxGeometry>();
    const materialCache = new Map<string, THREE.MeshBasicMaterial>();
    let shown = 0;
    let anyOutside = false;
    placements.forEach((placement) => {
      const d = placement.dimensions;
      const p = placement.position;
      if (Math.abs(p.x) + d.width / 2 > boxInches.width / 2 + 0.05 ||
          Math.abs(p.y) + d.height / 2 > boxInches.height / 2 + 0.05 ||
          Math.abs(p.z) + d.length / 2 > boxInches.length / 2 + 0.05) {
        anyOutside = true;
        return;
      }
      const geometryKey = `${d.width}x${d.height}x${d.length}`;
      let geometry = geometryCache.get(geometryKey);
      if (!geometry) {
        geometry = new THREE.BoxGeometry(d.width * VISUAL_INSET, d.height * VISUAL_INSET, d.length * VISUAL_INSET);
        geometryCache.set(geometryKey, geometry);
      }
      const color = getProductColor(placement.productIndex);
      let material = materialCache.get(color);
      if (!material) {
        material = new THREE.MeshBasicMaterial({ color, opacity: 0.7, transparent: true });
        materialCache.set(color, material);
      }
      const cube = new THREE.Mesh(geometry, material);
      cube.position.set(p.x, p.y, p.z);
      scene.add(cube);
      shown++;
    });

    setDisplayedCount(shown);
    setFitError(shown === 0 && (anyOutside || placements.length === 0));

    const maxDimension = Math.max(boxInches.width, boxInches.height, boxInches.length);
    camera.position.set(maxDimension * 0.8, maxDimension * 0.8, maxDimension * 1.8);
    camera.lookAt(0, 0, 0);

    let mounted = true;
    const animate = () => {
      if (!mounted) return;
      frameIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const w = container.clientWidth || 400;
      const h = container.clientHeight || 400;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      mounted = false;
      window.removeEventListener('resize', handleResize);
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
        frameIdRef.current = null;
      }
      clearScene(scene);
      controls.dispose();
      renderer.dispose();
      // dispose() ne libère PAS le contexte WebGL (le navigateur en limite le
      // nombre à ~16) : sans forceContextLoss, naviguer entre caisses/solutions
      // finit par épuiser les contextes et casser tous les rendus 3D.
      renderer.forceContextLoss();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, [placements, boxInches]);

  const firstOriented = placements.length > 0
    ? placements[0].dimensions
    : (boxInches && products.length > 0 ? orientedDimensions(toInches(products[0].dimensions, products[0].unit), orientation, boxInches) : null);

  const unitLabel = unitType === 'cm' ? 'cm' : 'in';

  return (
    <div className="box-visualization-container" style={{ position: 'relative', height: '400px', width: '100%', overflow: 'hidden', borderRadius: '6px', border: '1.5px solid #17222E', backgroundColor: '#E6E9ED' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

      <Chip
        icon={<RotateRightIcon fontSize="small" />}
        label={getOrientationLabel(orientation)}
        size="small"
        sx={{ position: 'absolute', top: 10, left: 10, zIndex: 10, backgroundColor: `${getOrientationColor(orientation)}22`, border: `1px solid ${getOrientationColor(orientation)}`, '& .MuiChip-icon': { color: getOrientationColor(orientation) } }}
      />

      <IconButton
        onClick={() => setShowLayoutDetails(!showLayoutDetails)}
        aria-label={showLayoutDetails ? 'Hide layout details' : 'Show layout details'}
        aria-expanded={showLayoutDetails}
        sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10, backgroundColor: 'rgba(255, 255, 255, 0.7)', padding: '4px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }, width: 28, height: 28 }}
        size="small"
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>

      {boxInches && (
        <Collapse in={showLayoutDetails}>
          <div style={{ position: 'absolute', top: '40px', right: '10px', maxWidth: '45%', backgroundColor: 'rgba(255, 255, 255, 0.85)', padding: '8px 12px', borderRadius: '4px', fontSize: '10px', zIndex: 10 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>Layout details:</Typography>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '10px' }}>
              <li style={{ fontSize: '10px', marginBottom: '3px' }}>Box (L×W×H): {displayValue(boxInches.length)} × {displayValue(boxInches.width)} × {displayValue(boxInches.height)} {unitLabel}</li>
              {firstOriented && (
                <li style={{ fontSize: '10px', marginBottom: '3px' }}>Product as packed (L×W×H): {displayValue(firstOriented.length)} × {displayValue(firstOriented.width)} × {displayValue(firstOriented.height)} {unitLabel}</li>
              )}
              <li style={{ fontSize: '10px', marginBottom: '3px' }}>Capacity: {solution.productsPerBox} product{solution.productsPerBox !== 1 ? 's' : ''} per box</li>
              <li style={{ fontSize: '10px', marginBottom: '3px' }}>Displayed in this box: {displayedCount}</li>
              <li style={{ fontSize: '10px', marginBottom: '3px' }}>Boxes needed: {solution.totalBoxesNeeded}</li>
            </ul>
          </div>
        </Collapse>
      )}

      <Typography variant="caption" component="div" sx={{ position: 'absolute', bottom: 60, left: 10, color: 'text.secondary', backgroundColor: 'rgba(255, 255, 255, 0.7)', padding: '4px 8px', borderRadius: '4px', maxWidth: '60%', maxHeight: '80px', overflow: 'auto' }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>Products:</Typography>
        <ul style={{ margin: 0, paddingLeft: '16px', marginTop: '4px', fontSize: '10px' }}>
          {products.map((p, index) => (
            <li key={p.id || index} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', fontSize: '10px' }}>
              <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: getProductColor(index), marginRight: '8px', borderRadius: '2px' }} />
              {p.quantity} × {displayProductValue(p.dimensions.length, p.unit)} × {displayProductValue(p.dimensions.width, p.unit)} × {displayProductValue(p.dimensions.height, p.unit)} {unitLabel} (L×W×H)
            </li>
          ))}
        </ul>
      </Typography>

      <Typography variant="caption" sx={{ position: 'absolute', bottom: 10, right: 10, color: 'text.secondary', backgroundColor: 'rgba(255, 255, 255, 0.7)', padding: '2px 6px', borderRadius: '4px' }}>
        Drag to rotate, scroll to zoom
      </Typography>

      {fitError && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#c62828', fontWeight: 'bold', padding: '10px', backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: '5px', textAlign: 'center', zIndex: 100, maxWidth: '80%' }}>
          The 3D layout for this box could not be displayed.
        </div>
      )}

      <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', backgroundColor: 'rgba(255, 255, 255, 0.7)', padding: '4px 10px', borderRadius: '12px', zIndex: 20 }}>
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#103A63' }}>Box {currentBoxIndex + 1} of {totalBoxes}</span>
        {/* Les caps 3D (50 caisses / 500 placements) ne changent pas les comptes,
            mais l'utilisateur doit savoir que l'aperçu est tronqué. */}
        {(solution.totalBoxesNeeded || 0) > totalBoxes && (
          <span style={{ fontSize: '10px', color: '#103A63' }}>
            3D preview limited to {totalBoxes} boxes · {solution.totalBoxesNeeded} needed in total
          </span>
        )}
        {displayedCount >= 500 && (
          <span style={{ fontSize: '10px', color: '#103A63' }}>
            3D shows the first 500 items of this box
          </span>
        )}
      </div>

      {totalBoxes > 1 && (
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)', display: 'flex', justifyContent: 'space-between', padding: '0 16px', zIndex: 10, pointerEvents: 'none' }}>
          <button onClick={goToPreviousBox} disabled={currentBoxIndex === 0} aria-label="Previous box" style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: currentBoxIndex === 0 ? '#e0e0e0' : '#103A63', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentBoxIndex === 0 ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)', opacity: currentBoxIndex === 0 ? 0.5 : 1, pointerEvents: 'auto' }}>&#10094;</button>
          <button onClick={goToNextBox} disabled={currentBoxIndex === totalBoxes - 1} aria-label="Next box" style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: currentBoxIndex === totalBoxes - 1 ? '#e0e0e0' : '#103A63', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentBoxIndex === totalBoxes - 1 ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)', opacity: currentBoxIndex === totalBoxes - 1 ? 0.5 : 1, pointerEvents: 'auto' }}>&#10095;</button>
        </div>
      )}
    </div>
  );
};

export default BoxVisualization;
